import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, UserMinus, ShieldCheck, Sparkles } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import CharacterCard from "@/components/CharacterCard";
import BottomNav from "@/components/BottomNav";

/**
 * Public profile at /u/:username. Shows avatar, bio, follow counts, follow
 * button, and the user's public characters. If viewing your own profile,
 * the Follow button is replaced with "Edit profile" linking to /profile.
 */
export default function PublicProfile() {
  const { username } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/users/${username}`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Profile not found.");
        nav(-1);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [username, nav]);

  async function toggleFollow() {
    if (!data) return;
    setBusy(true);
    try {
      if (data.is_following) {
        const { data: r } = await api.delete(`/users/${username}/follow`);
        setData((d) => ({ ...d, is_following: false, followers: r.followers }));
      } else {
        const { data: r } = await api.post(`/users/${username}/follow`);
        setData((d) => ({ ...d, is_following: true, followers: r.followers }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update follow.");
    } finally {
      setBusy(false);
    }
  }

  if (loading || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 pt-safe-6 pb-32">
        <div className="animate-pulse text-neutral-500">Loading…</div>
      </div>
    );
  }

  const u = data.user;

  return (
    <div className="mx-auto max-w-2xl px-4 pt-safe-6 pb-32">
      <button
        data-testid="public-profile-back"
        onClick={() => nav(-1)}
        className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
      >
        <ArrowLeft size={18} />
      </button>

      <header className="mb-6 flex items-start gap-4">
        <UserAvatar src={u.avatar_url} name={u.username} size={88} testId="public-profile-avatar" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 data-testid="public-profile-username" className="font-display text-2xl text-white leading-tight break-words">
              @{u.username}
            </h1>
            {u.is_plus && (
              <span
                data-testid="public-profile-plus-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-200"
                title="Immersive+ founding member"
              >
                <Sparkles size={10} /> Founder
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span data-testid="public-profile-followers"><span className="font-medium text-white">{data.followers}</span> <span className="text-neutral-400">followers</span></span>
            <span data-testid="public-profile-following"><span className="font-medium text-white">{data.following}</span> <span className="text-neutral-400">following</span></span>
          </div>
          <div className="mt-3">
            {data.is_self ? (
              <Link
                to="/profile"
                data-testid="public-profile-edit-btn"
                className="btn-pill btn-ghost text-xs py-2 px-4 inline-flex"
              >
                Edit profile
              </Link>
            ) : (
              <button
                data-testid={data.is_following ? "public-profile-unfollow-btn" : "public-profile-follow-btn"}
                onClick={toggleFollow}
                disabled={busy}
                className={`btn-pill text-xs py-2 px-4 inline-flex items-center gap-1.5 ${
                  data.is_following ? "btn-ghost" : "btn-primary"
                }`}
              >
                {data.is_following ? <><UserMinus size={14} /> Following</> : <><UserPlus size={14} /> Follow</>}
              </button>
            )}
          </div>
        </div>
      </header>

      {u.bio ? (
        <p data-testid="public-profile-bio" className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-sm text-neutral-200 whitespace-pre-wrap">
          {u.bio}
        </p>
      ) : (
        <p className="mb-6 text-xs text-neutral-500">No bio yet.</p>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-lg text-white">Characters</h2>
        <span className="text-xs text-neutral-500">{data.characters.length}</span>
      </div>

      {data.characters.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-neutral-500">
          @{u.username} hasn't published any characters yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="public-profile-chars-grid">
          {data.characters.map((c) => (
            <CharacterCard key={c.id} character={c} testIdPrefix="public-profile" />
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
